import { Crown, Pencil, ShieldCheck, Trash2 } from 'lucide-react';
import { Badge, Button } from '@ncoder/ui';
import type { OrgRole } from '@/services/org-admin';

export interface RoleRowProps {
  role: OrgRole;
  onEdit: (role: OrgRole) => void;
  onDelete: (role: OrgRole) => void;
  isDeleting: boolean;
}

const LEVEL_ICON = { owner: Crown, admin: ShieldCheck } as const;

export function RoleRow({ role, onEdit, onDelete, isDeleting }: RoleRowProps) {
  const LevelIcon = role.management_level !== 'member' ? LEVEL_ICON[role.management_level] : null;
  return (
    <div className="flex h-full flex-col rounded-lg border border-border/60 bg-card/50 p-4 transition-colors hover:border-border">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold">{role.name}</span>
          {role.is_default && (
            <Badge
              variant="outline"
              className="shrink-0 border-transparent bg-accent text-accent-foreground"
              title="Automatically created when this org was set up — every org has one. Not auto-assigned to new members; assign it like any other role."
            >
              Default
            </Badge>
          )}
          {LevelIcon && (
            <Badge
              variant="outline"
              className="shrink-0 gap-1 capitalize"
              title="Management level — determines admin authority for whoever holds this role, in addition to its granted services."
            >
              <LevelIcon className="h-3 w-3" />
              {role.management_level}
            </Badge>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(role)} title="Edit role">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(role)}
            disabled={role.is_default || isDeleting}
            title={role.is_default ? 'The default role cannot be deleted' : 'Delete role'}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-1 flex-col gap-1.5 rounded-md border border-border/40 bg-muted/20 p-2.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Access
        </span>
        <div className="flex flex-wrap content-start gap-1.5">
          {role.capabilities.length === 0 ? (
            <span className="text-xs text-muted-foreground">No services granted</span>
          ) : (
            role.capabilities.map((capability) => (
              <Badge key={capability.key} variant="outline" className="text-xs font-normal">
                {capability.label}
              </Badge>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
